// Firebase Cloud Messaging (FCM) helpers for the browser.
//
// Exposes the same surface the UI already uses:
//   pushSupported() · isSubscribed() · enablePush() · disablePush() · sendTestPush()
//
// The Firebase web config is fetched from the backend (/api/push/config) so it
// stays env-driven; the service worker receives it via query-string params.

import { initializeApp, getApps } from "firebase/app";
import {
  getMessaging,
  getToken,
  deleteToken,
  onMessage,
  isSupported,
} from "firebase/messaging";
import api from "../api";

const TOKEN_KEY = "fcmToken";

let messaging = null;
let foregroundBound = false;

export function pushSupported() {
  return (
    "serviceWorker" in navigator &&
    "Notification" in window &&
    "PushManager" in window
  );
}

export function permission() {
  return pushSupported() ? Notification.permission : "unsupported";
}

export async function isSubscribed() {
  return (
    pushSupported() &&
    Notification.permission === "granted" &&
    !!localStorage.getItem(TOKEN_KEY)
  );
}

// Initialise Firebase from the backend config and return a messaging instance
// plus the service-worker registration (config passed to the SW via query).
async function init() {
  const cfg = await api.get("/push/config");
  if (!cfg.configured || !cfg.firebase?.apiKey) {
    return { ok: false, reason: "not-configured" };
  }
  if (!(await isSupported())) return { ok: false, reason: "unsupported" };

  const fb = cfg.firebase;
  if (!getApps().length) {
    initializeApp({
      apiKey: fb.apiKey,
      authDomain: fb.authDomain,
      projectId: fb.projectId,
      storageBucket: fb.storageBucket,
      messagingSenderId: fb.messagingSenderId,
      appId: fb.appId,
    });
  }
  messaging = getMessaging();

  // The SW has the Firebase config hard-coded, so no query string is needed.
  const registration = await navigator.serviceWorker.register(
    "/firebase-messaging-sw.js"
  );
  await navigator.serviceWorker.ready;

  // Show a notification while the app is in the foreground too (FCM does not
  // auto-display when a tab is focused).
  if (!foregroundBound) {
    foregroundBound = true;
    onMessage(messaging, (payload) => {
      const n = payload.notification || {};
      const d = payload.data || {};
      if (Notification.permission === "granted") {
        const note = new Notification(n.title || "RABS Domain Hub", {
          body: n.body || "",
          icon: "/logo192.png",
        });
        note.onclick = () => {
          window.focus();
          if (d.url) window.location.assign(d.url);
          note.close();
        };
      }
    });
  }

  return { ok: true, registration, vapidKey: fb.vapidKey };
}

export async function enablePush() {
  if (!pushSupported()) return { ok: false, reason: "unsupported" };

  const perm = await Notification.requestPermission();
  if (perm !== "granted") return { ok: false, reason: "denied" };

  const ready = await init();
  if (!ready.ok) return ready;

  try {
    const token = await getToken(messaging, {
      vapidKey: ready.vapidKey,
      serviceWorkerRegistration: ready.registration,
    });
    if (!token) return { ok: false, reason: "no-token" };
    await api.post("/push/subscribe", { token });
    localStorage.setItem(TOKEN_KEY, token);
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: err.message || "subscribe-failed" };
  }
}

export async function disablePush() {
  const token = localStorage.getItem(TOKEN_KEY);
  try {
    if (token) {
      await api.post("/push/unsubscribe", { token }).catch(() => {});
    }
    if (messaging) {
      await deleteToken(messaging).catch(() => {});
    }
    localStorage.removeItem(TOKEN_KEY);
    return { ok: true };
  } catch (err) {
    localStorage.removeItem(TOKEN_KEY);
    return { ok: false, reason: err.message || "unsubscribe-failed" };
  }
}

// Called automatically after login. Stores/refreshes this device's FCM token
// in the database. It only prompts for permission if push is actually
// configured on the server and permission hasn't been decided yet; if the user
// has blocked notifications, or push isn't set up, it quietly does nothing.
export async function autoRegisterPush(promptIfNeeded = false) {
  if (!pushSupported()) return { ok: false, reason: "unsupported" };
  if (Notification.permission === "denied") {
    return { ok: false, reason: "denied" };
  }
  // On a silent run (e.g. page refresh) only proceed if already allowed —
  // don't pop the permission dialog. Fresh logins pass promptIfNeeded=true.
  if (Notification.permission !== "granted" && !promptIfNeeded) {
    return { ok: false, reason: "not-granted" };
  }

  // init() checks server config + browser support and registers the SW.
  const ready = await init();
  if (!ready.ok) return ready; // e.g. not-configured

  if (Notification.permission !== "granted") {
    const perm = await Notification.requestPermission();
    if (perm !== "granted") return { ok: false, reason: "denied" };
  }

  try {
    const token = await getToken(messaging, {
      vapidKey: ready.vapidKey,
      serviceWorkerRegistration: ready.registration,
    });
    if (!token) return { ok: false, reason: "no-token" };
    await api.post("/push/subscribe", { token });
    localStorage.setItem(TOKEN_KEY, token);
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: err.message || "subscribe-failed" };
  }
}

// Re-establish the Firebase messaging + foreground onMessage handler after a
// page load, so notifications still show while the app tab is focused. Safe to
// call on every mount — it never requests permission or a new token.
export async function primeForeground() {
  if (!(await isSubscribed())) return;
  try {
    await init();
  } catch {
    // ignore — push just won't show in the foreground until re-enabled
  }
}

export async function sendTestPush() {
  return api.post("/push/test");
}
